/* eslint-disable cypress/no-assigning-return-values */
import { CodeBracketIcon } from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import { ReactElement } from "react";

import IconAlert from "../../assets/icons/alert.svg";
import IconCircleCheck from "../../assets/icons/circle-check.svg";
import IconProgress from "../../assets/icons/progress.svg";
import { useEntityCache } from "../../hooks/useEntityCache.ts";
import { $cytoscapeInstance } from "../../stores/graph/ui.ts";
import { $workflowID } from "../../stores/graph/workflow-result.ts";
import { $workflowsByGraphID } from "../../stores/workflows.ts";
import { GraphEdgeStatusFilterType } from "../../types/graph.ts";
import { Button } from "../catalyst/Button.tsx";
import { ErrorMessage } from "../catalyst/ErrorMessage.tsx";
import { Table } from "../catalyst/Table.tsx";
import { TableBody } from "../catalyst/TableBody.tsx";
import { TableCell } from "../catalyst/TableCell.tsx";
import { TableHead } from "../catalyst/TableHead.tsx";
import { TableHeader } from "../catalyst/TableHeader.tsx";
import { TableRow } from "../catalyst/TableRow.tsx";

interface FloatingLogsTableProps {
  statusFilter: GraphEdgeStatusFilterType;
}

export const FloatingLogsTable = ({
  statusFilter,
}: FloatingLogsTableProps): ReactElement => {
  const { data, error, loading } = useStore($workflowsByGraphID);
  const cy = useStore($cytoscapeInstance);
  const { getEntityById } = useEntityCache();

  const filteredWorkflows = (data ?? []).filter((workflow) =>
    statusFilter === "all" ? true : workflow?.status === statusFilter,
  );

  const getStatusIcon = (status: string): null | ReactElement => {
    if (status === "SUCCEEDED") {
      return <IconCircleCheck className="h-4 w-4 text-emerald-500" />;
    }
    if (status === "FAILED") {
      return <IconAlert className="h-4 w-4 text-rose-500" />;
    }
    if (status === "RUNNING") {
      return <IconProgress className="h-4 w-4 animate-spin text-sky-500" />;
    }
    return null;
  };

  const getNodeType = (entityId: string): string => {
    if (!entityId) return "";
    const entity = getEntityById(entityId);
    return entity?.name || "";
  };

  const handleResponseClick = (id: string): void => {
    $workflowID.set(id);
  };

  const handleRowClick = (sourceId?: string): void => {
    if (!cy || !sourceId) return;
    cy.elements().removeClass("highlighted");
    const node = cy.getElementById(sourceId);
    if (node.length > 0) {
      node.addClass("highlighted");
      cy.center(node);
    }
  };

  if (loading && !data) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            className="flex animate-pulse items-center space-x-4 p-2"
            key={index}
          >
            <div className="h-4 w-20 rounded bg-zinc-700" />
            <div className="h-4 w-16 rounded bg-zinc-700" />
            <div className="h-4 w-24 rounded bg-zinc-700" />
            <div className="h-4 w-32 rounded bg-zinc-700" />
            <div className="h-4 w-16 rounded bg-zinc-700" />
          </div>
        ))}
      </div>
    );
  }

  if (!loading && data?.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-zinc-400">
        No logs found
      </div>
    );
  }

  if (!loading && error) {
    return <ErrorMessage>Error loading logs</ErrorMessage>;
  }

  return (
    <div className="space-y-1">
      <div className="max-h-[144px] overflow-y-auto">
        <Table dense>
          <TableHead>
            <TableRow>
              <TableHeader className="w-[15%] text-xs">Time</TableHeader>
              <TableHeader className="w-[15%] text-xs">ID</TableHeader>
              <TableHeader className="w-[15%] text-xs">Query</TableHeader>
              <TableHeader className="w-[30%] text-xs">Source Node</TableHeader>
              <TableHeader className="w-[13%] text-xs">Status</TableHeader>
              <TableHeader className="w-[10%] text-xs">Response</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWorkflows.map((workflow) => (
              <TableRow
                className="cursor-pointer hover:bg-zinc-800/50"
                key={workflow.id}
                onClick={() => handleRowClick(workflow.sourceId)}
              >
                <TableCell className="w-[15%] truncate text-xs">
                  {workflow.timestamp?.toLocaleString() || "N/A"}
                </TableCell>
                <TableCell className="w-[15%] truncate text-xs">
                  {workflow.id}
                </TableCell>
                <TableCell className="w-[15%] truncate text-xs">
                  {workflow.title}
                </TableCell>
                <TableCell className="w-[30%] truncate text-xs">
                  {cy && workflow.sourceId && (
                    <>
                      {cy.getElementById(workflow.sourceId).data("value") ||
                        workflow.sourceId}
                      {" ["}
                      {getNodeType(
                        cy.getElementById(workflow.sourceId).data("entityId"),
                      )}
                      ]
                    </>
                  )}
                </TableCell>
                <TableCell className="w-[13%] text-xs">
                  <div className="flex items-center gap-1">
                    {getStatusIcon(workflow.status)}
                    <span className="truncate">{workflow.status}</span>
                  </div>
                </TableCell>
                <TableCell className="w-[10%]">
                  <Button
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleResponseClick(workflow.id);
                    }}
                    outline
                    size="xs"
                  >
                    <CodeBracketIcon className="fill-white" />
                    Payload
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
